import {
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  Stack,
  TextField
} from "@fluentui/react";
import { Field, Form, Formik } from "formik";
import { useState } from "react";
import { useAsyncCallback } from "react-async-hook";
import * as Yup from "yup";

import TagPicture from "./TagPicture";

import { ContractsService, ProductsService } from "@/core/libs/api";

const productsService = ProductsService();
const contractsService = ContractsService();

interface TagReplacementDialogProps {
  item: any;
  service: number;
  businessUnitId: number;
  onSuccess: () => void;
  disabled: boolean;
}

function TagReplacementDialog({
  item,
  service,
  businessUnitId: businessActionId,
  onSuccess,
  disabled
}: TagReplacementDialogProps) {
  const [isDialogHidden, setIsDialogHidden] = useState(true);
  const [inventoryData, setInventoryData] = useState<{
    inventoryId: number;
    serialNumber: string;
    brandName: string;
  }>(undefined);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );

  const replaceItem = useAsyncCallback(
    () => {
      setErrorMessage(undefined);
      const abortController = new AbortController();
      const { signal } = abortController;

      return contractsService.replaceItem({
        params: { id: item.id, body: { inventory: inventoryData.inventoryId } },
        signal
      });
    },
    {
      onSuccess({ response, error }) {
        if (response.ok) {
          setIsDialogHidden(true);
          onSuccess();
        } else {
          setErrorMessage(error.message);
        }
      },
      onError(error) {
        setErrorMessage(error.message);
      }
    }
  );

  const tagIsCanceled = item?.inventory[0]?.canceledAtStartag;

  return (
    <>
      <PrimaryButton
        text="Substituir"
        onClick={() => setIsDialogHidden(false)}
        disabled={disabled || item.nextStep || tagIsCanceled}
      />

      <Dialog
        hidden={isDialogHidden}
        onDismiss={() => setIsDialogHidden(true)}
        modalProps={{ onDismissed: () => setInventoryData(undefined) }}
        dialogContentProps={
          inventoryData
            ? {
                type: DialogType.normal,
                title: "Deseja Substituir a Tag?",
                closeButtonAriaLabel: "Cancelar",
                subText:
                  "Confira o novo número de série e confirme a substituição. Essa ação é irreversível."
              }
            : {
                type: DialogType.normal,
                title: "Substituição de Tag",
                closeButtonAriaLabel: "Cancelar",
                subText:
                  "Insira o número de série da nova tag, a qual substituirá a atual."
              }
        }
      >
        {!inventoryData && (
          <Formik
            initialValues={{ serialNumber: "" }}
            validationSchema={Yup.object({
              serialNumber: Yup.string()
                .required("Campo obrigatório")
                .length(16, "O número de série deve ter 16 dígitos")
            })}
            onSubmit={({ serialNumber }, { setSubmitting, setErrors }) => {
              setErrorMessage(undefined);
              const abortController = new AbortController();
              const { signal } = abortController;

              productsService
                .searchProduct({
                  params: {
                    query: {
                      businessActionId,
                      serialNumber,
                      operation: item.operation.id,
                      service
                    }
                  },
                  signal
                })
                .then(({ data, error }) => {
                  if (error) {
                    if (error.status === 400) {
                      return setErrors({
                        serialNumber:
                          "O número de série não existe ou já está sendo utilizado"
                      });
                    }

                    return setErrorMessage(error.message);
                  }
                  setInventoryData({
                    inventoryId: data.inventory.id,
                    serialNumber: data.inventory.serialNumber,
                    brandName: data.brand.name
                  });
                })
                .finally(() => setSubmitting(false));
            }}
          >
            {({ errors, touched, isSubmitting }) => (
              <Form>
                <Stack tokens={{ childrenGap: 15 }}>
                  <Field
                    as={TextField}
                    name="serialNumber"
                    label="Novo número de série"
                    required
                    maxLength={16}
                    inputMode="numeric"
                    errorMessage={touched.serialNumber && errors.serialNumber}
                  />

                  {errorMessage && (
                    <MessageBar messageBarType={MessageBarType.error}>
                      {errorMessage}
                    </MessageBar>
                  )}
                </Stack>

                <DialogFooter>
                  <PrimaryButton
                    type="submit"
                    text="Continuar"
                    disabled={isSubmitting}
                  />
                  <DefaultButton
                    onClick={() => setIsDialogHidden(true)}
                    text="Cancelar"
                    disabled={isSubmitting}
                  />
                </DialogFooter>
              </Form>
            )}
          </Formik>
        )}

        {inventoryData && (
          <>
            <Stack tokens={{ childrenGap: 15 }}>
              <TagPicture
                serialNumber={inventoryData.serialNumber}
                brandName={inventoryData.brandName}
              />
              {errorMessage && (
                <MessageBar messageBarType={MessageBarType.error}>
                  {errorMessage}
                </MessageBar>
              )}
            </Stack>
            <DialogFooter>
              <PrimaryButton
                onClick={() => replaceItem.execute()}
                text="Substituir"
                disabled={replaceItem.loading}
              />
              <DefaultButton
                onClick={() => setIsDialogHidden(true)}
                text="Cancelar"
                disabled={replaceItem.loading}
              />
            </DialogFooter>
          </>
        )}
      </Dialog>
    </>
  );
}

export default TagReplacementDialog;
